<template>
  <b-modal :visible="true" centered :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy"
           @hidden="$emit('close')" @hide="onHide" body-class="cd-modal-body">
    <template #modal-title>
      <template v-if="mode === 'archive'"><i class="fas fa-archive text-danger mr-1"></i> Archive {{ company.name }}?</template>
      <template v-else-if="mode === 'restore'"><i class="fas fa-undo text-success mr-1"></i> Restore {{ company.name }}?</template>
      <template v-else><i class="fas fa-trash-alt text-danger mr-1"></i> Delete {{ company.name }} for good?</template>
    </template>

    <template v-if="mode === 'archive'">
      <p class="cd-intro">The company disappears from the list, but nothing is lost: you can bring it back from the <b>Archived</b> tab.</p>
      <ul class="cd-notes">
        <li>Its <b>{{ fmt(company.assignedTrees) }}</b> assigned tree{{ company.assignedTrees === 1 ? '' : 's' }} go back to the free pool.</li>
        <li>Its API token stops working (it is kept, and works again if you restore the company).</li>
        <li>Its users are blocked and cannot sign in.</li>
      </ul>
      <p v-if="loadingUsers" class="cd-intro"><i class="fas fa-circle-notch fa-spin"></i> Checking its users…</p>
      <div v-else-if="activeUsers.length" class="cd-warn">
        <b>{{ activeUsers.length }} user{{ activeUsers.length === 1 ? '' : 's' }} will be blocked:</b>
        <ul>
          <li v-for="u in activeUsers" :key="u.id">{{ u.firstName || u.email }}<span v-if="u.firstName" class="text-muted"> · {{ u.email }}</span><span v-if="u.isCompanyOwner" class="cd-chip">Owner</span></li>
        </ul>
      </div>
      <p v-else class="cd-intro">This company has no active users.</p>
    </template>

    <template v-else-if="mode === 'restore'">
      <p class="cd-intro">The company comes back with the same API token, and the users that the archive blocked are unblocked.</p>
      <label class="cd-check">
        <input v-model="reassign" type="checkbox">
        <span>Assign its previous trees again (only those that are still free)</span>
      </label>
    </template>

    <template v-else>
      <p class="cd-intro">
        This <b>cannot be undone</b>. The company and its API token are deleted. Its transfers are kept and will show "No partner".
        Its users stay blocked, without a company.
      </p>
      <label class="cd-fld">
        <span>To confirm, type {{ company.name }}</span>
        <input v-model="typed" type="text" autocomplete="off" @keyup.enter="confirm">
      </label>
    </template>

    <div v-if="error" class="cd-error"><i class="fas fa-exclamation-circle"></i> {{ error }}</div>

    <template #modal-footer>
      <button type="button" class="cd-btn ghost" :disabled="busy" @click="$emit('close')">Cancel</button>
      <button type="button" class="cd-btn" :class="mode === 'restore' ? 'primary' : 'danger'" :disabled="!ready || busy" @click="confirm">
        <i v-if="busy" class="fas fa-circle-notch fa-spin"></i>
        {{ busy ? 'Working…' : (mode === 'archive' ? 'Archive' : mode === 'restore' ? 'Restore' : 'Delete for good') }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import Companies, { apiError } from '../../api/Companies';

/** Archive (the reversible way to "delete"), restore, and delete an archived company for good. */
export default {
  name: 'CompanyActionDialog',
  props: {
    mode: { type: String, required: true }, // 'archive' | 'restore' | 'delete'
    company: { type: Object, required: true }, // { id, name, assignedTrees }
  },
  data() {
    return { busy: false, error: '', typed: '', reassign: true, loadingUsers: false, users: [] };
  },
  computed: {
    activeUsers() { return this.users.filter((u) => !u.blocked); },
    ready() {
      if (this.mode === 'delete') return this.typed.trim().toLowerCase() === String(this.company.name).trim().toLowerCase();
      if (this.mode === 'archive') return !this.loadingUsers;
      return true;
    },
  },
  async created() {
    if (this.mode !== 'archive') return;
    this.loadingUsers = true;
    try {
      this.users = ((await Companies.get(this.company.id)).data || {}).members || [];
    } catch (err) {
      this.users = [];
    } finally {
      this.loadingUsers = false;
    }
  },
  methods: {
    fmt(v) { return new Intl.NumberFormat('en-GB').format(Number(v) || 0); },
    onHide(e) { if (this.busy) e.preventDefault(); },
    async confirm() {
      if (!this.ready || this.busy) return;
      this.busy = true;
      this.error = '';
      const name = this.company.name;
      try {
        const res = this.mode === 'archive' ? await Companies.archive(this.company.id)
          : this.mode === 'restore' ? await Companies.restore(this.company.id, this.reassign)
            : await Companies.remove(this.company.id, this.typed);
        const d = res.data || {};
        this.$emit('done', this.mode === 'archive'
          ? `${name} was archived: ${d.releasedTrees || 0} trees released, ${d.blockedUsers || 0} users blocked.`
          : this.mode === 'restore'
            ? `${name} was restored: ${d.unblockedUsers || 0} users unblocked, ${d.reassignedTrees || 0} trees assigned again${d.treesNoLongerFree ? ` (${d.treesNoLongerFree} no longer free)` : ''}.`
            : `${name} was deleted.`);
      } catch (err) {
        this.error = apiError(err, 'Could not complete the operation.');
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import './dialogs.scss';
</style>
